import { createContext, useState, useCallback, useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { apiV1 } from "../lib/axios";
import { toast } from "react-toastify";
import { userHasPermission } from "../utils/verifyUserPermission";
import { companyUserPermissions } from "../enums/companyUserPermissions";

export const PagamentoLoteContext = createContext({});

export function PagamentoLoteProvider() {
  const { user, profileLoading } = useAuth();
  const [payments, setPayments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [payees, setPayees] = useState([]);
  const [apiStats, setApiStats] = useState({
    total:     { count: 0, amount: 0 },
    approved:  { count: 0, amount: 0 },
    pending:   { count: 0, amount: 0 },
    alert:     { count: 0, amount: 0 },
    scheduled: { count: 0, amount: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("todos");
  const [searchTerm, setSearchTerm] = useState("");

  // ── carregamento inicial ───────────────────────────────────────────────────

  useEffect(() => {
    if (!profileLoading && user?.isMainAccess && user?.type === "1") {
      loadBatches();
      loadPayees();
      loadStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading]);

  async function loadPayments(batchId) {
    try {
      setLoading(true);
      const params = batchId ? `?batchId=${batchId}` : "";
      const response = await apiV1.get(`/batch-payments/payments${params}`);
      const data = (response.data.data || []).map((p) => ({ ...p, selected: false }));
      setPayments(data);
    } catch (err) {
      console.error("Erro ao carregar pagamentos:", err);
      toast.error("Erro ao carregar pagamentos do lote.");
    } finally {
      setLoading(false);
    }
  }

  async function loadBatches() {
    try {
      const response = await apiV1.get("/batch-payments/batches");
      setBatches(response.data.data || []);
    } catch (err) {
      console.error("Erro ao carregar lotes:", err);
      toast.error("Erro ao carregar lotes.");
    }
  }

  async function loadPayees() {
    try {
      const response = await apiV1.get("/batch-payments/payees");
      setPayees(response.data.data || []);
    } catch (err) {
      console.error("Erro ao carregar favorecidos:", err);
    }
  }

  async function loadStats() {
    try {
      const response = await apiV1.get("/batch-payments/stats");
      setApiStats(response.data.data);
    } catch (err) {
      console.error("Erro ao carregar estatísticas:", err);
    }
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  const sumBy = (list) => list.reduce((acc, item) => acc + Number(item.amount || 0), 0);

  const filteredPayments = useCallback(() => {
    const search = searchTerm.toLowerCase().trim();
    return payments.filter((item) => {
      const matchesFilter = currentFilter === "todos" || item.status === currentFilter;
      const matchesSearch =
        !search ||
        item.payee.toLowerCase().includes(search) ||
        item.document.toLowerCase().includes(search) ||
        String(item.amount).includes(search.replace(",", "."));
      return matchesFilter && matchesSearch;
    });
  }, [payments, currentFilter, searchTerm]);

  // ── stats locais (seleção) + merge com stats da API ──────────────────────

  const stats = (() => {
    const selected = payments.filter((p) => p.selected);
    return {
      ...apiStats,
      selected:         { count: selected.length,                                        amount: sumBy(selected) },
      selectedApproved: { count: selected.filter(p => p.status === "Aprovado").length,   amount: sumBy(selected.filter(p => p.status === "Aprovado")) },
      selectedPending:  { count: selected.filter(p => p.status === "Pendente").length,   amount: sumBy(selected.filter(p => p.status === "Pendente")) },
      selectedAlert:    { count: selected.filter(p => p.status === "Alerta").length,     amount: sumBy(selected.filter(p => p.status === "Alerta")) },
    };
  })();

  // ── actions ────────────────────────────────────────────────────────────────

  const toggleItem = useCallback((id, checked) => {
    setPayments((prev) =>
      prev.map((item) => (item.id === id ? { ...item, selected: checked } : item))
    );
  }, []);

  const toggleAll = useCallback((checked) => {
    const visibleIds = new Set(filteredPayments().map((p) => p.id));
    setPayments((prev) =>
      prev.map((item) => (visibleIds.has(item.id) ? { ...item, selected: checked } : item))
    );
  }, [filteredPayments]);

  // ── guards (após todos os hooks) ──────────────────────────────────────────

  if (profileLoading) return null;
  if (!user?.isMainAccess || user?.type !== "1") {
    return <Navigate to="/pagina-principal" replace />;
  }

  async function sendBatchMfa() {
    try {
      await apiV1.post("/batch-payments/batches/mfa");
      return { ok: true };
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao enviar o código MFA.");
      return { ok: false };
    }
  }

  async function submitBatch(batchId) {
    try {
      setLoading(true);
      const response = await apiV1.patch(`/batch-payments/batches/${batchId}/submit`);
      const updated = response.data.data;
      setBatches((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
      toast.success("Lote submetido para aprovação!");
      return { ok: true };
    } catch (err) {
      console.error("Erro ao submeter lote:", err);
      toast.error(err?.response?.data?.message || "Erro ao submeter o lote.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function approveSelected(batchId, otp) {
    const selected = payments.filter((p) => p.selected);
    if (!selected.length) {
      toast.warning("Selecione ao menos uma conta para aprovar.");
      return { ok: false };
    }
    const withAlert = selected.filter((p) => p.status === "ALERTA");
    if (withAlert.length) {
      toast.error("Existem contas com alerta. Remova-as antes de aprovar.");
      return { ok: false };
    }
    // Permission guard: sub-access must have APPROVE_BATCH_PAYMENT permission
    if (!user.isMainAccess && !userHasPermission(user, companyUserPermissions.APPROVE_BATCH_PAYMENT)) {
      toast.error("Você não tem permissão para aprovar pagamentos em lote.");
      return { ok: false };
    }
    // Four-eyes: sub-access cannot approve their own batch
    const batch = batches.find((b) => String(b.id) === String(batchId));
    if (!user.isMainAccess && batch?.submittedBy === user?.companyUser?.id) {
      toast.error("O responsável pelo envio do lote não pode aprová-lo.");
      return { ok: false };
    }
    try {
      setLoading(true);
      await apiV1.patch(`/batch-payments/batches/${batchId}/approve`, {
        paymentIds: selected.map((p) => p.id),
        otp,
      });
      toast.success("Pagamentos aprovados e executados com sucesso!");
      await Promise.all([loadPayments(batchId), loadBatches(), loadStats()]);
      return { ok: true };
    } catch (err) {
      console.error("Erro ao aprovar:", err);
      toast.error(err?.response?.data?.message || "Erro ao aprovar os pagamentos.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function addPayment(data) {
    try {
      setLoading(true);
      await apiV1.post("/batch-payments/payments", data);
      await Promise.all([
        loadPayments(data.batchId),
        loadBatches(),
        loadStats(),
      ]);
      return { ok: true };
    } catch (err) {
      console.error("Erro ao adicionar pagamento:", err);
      toast.error(err?.response?.data?.message || "Erro ao adicionar pagamento.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function removePayment(id) {
    try {
      setLoading(true);
      await apiV1.delete(`/batch-payments/payments/${id}`);
      setPayments((prev) => prev.filter((p) => p.id !== id));
      await Promise.all([loadBatches(), loadStats()]);
    } catch (err) {
      console.error("Erro ao remover pagamento:", err);
      toast.error(err?.response?.data?.message || "Erro ao remover o pagamento.");
    } finally {
      setLoading(false);
    }
  }

  async function createBatch(data = {}) {
    try {
      setLoading(true);
      const response = await apiV1.post("/batch-payments/batches", data);
      const newBatch = response.data.data;
      setBatches((prev) => [newBatch, ...prev]);
      return { ok: true, batch: newBatch };
    } catch (err) {
      console.error("Erro ao criar lote:", err);
      toast.error(err?.response?.data?.message || "Erro ao criar o lote.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function deleteBatch(batchId) {
    try {
      setLoading(true);
      await apiV1.delete(`/batch-payments/batches/${batchId}`);
      setBatches((prev) => prev.filter((b) => b.id !== batchId));
      await loadStats();
      toast.success("Lote excluído com sucesso!");
      return { ok: true };
    } catch (err) {
      console.error("Erro ao excluir lote:", err);
      toast.error(err?.response?.data?.message || "Erro ao excluir o lote.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function rejectBatch(batchId, reason) {
    try {
      setLoading(true);
      await apiV1.patch(`/batch-payments/batches/${batchId}/reject`, { reason });
      await Promise.all([loadBatches(), loadStats()]);
      toast.success("Lote rejeitado com sucesso!");
      return { ok: true };
    } catch (err) {
      console.error("Erro ao rejeitar lote:", err);
      toast.error(err?.response?.data?.message || "Erro ao rejeitar o lote.");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }

  async function addPayee(data) {
    try {
      setLoading(true);
      const response = await apiV1.post("/batch-payments/payees", data);
      setPayees((prev) => [response.data.data, ...prev]);
      toast.success("Favorecido cadastrado com sucesso!");
    } catch (err) {
      console.error("Erro ao cadastrar favorecido:", err);
      toast.error(err?.response?.data?.message || "Erro ao cadastrar favorecido.");
    } finally {
      setLoading(false);
    }
  }

  // ── stats antigos removidos — agora em apiStats + selectionStats acima ──────

  return (
    <PagamentoLoteContext.Provider
      value={{
        payments,
        batches,
        payees,
        stats,
        loading,
        currentFilter,
        setCurrentFilter,
        searchTerm,
        setSearchTerm,
        filteredPayments,
        toggleItem,
        toggleAll,
        approveSelected,
        sendBatchMfa,
        submitBatch,
        createBatch,
        deleteBatch,
        rejectBatch,
        addPayment,
        removePayment,
        addPayee,
        loadPayments,
        loadBatches,
        loadPayees,
        loadStats,
        sumBy,
      }}
    >
      <Outlet />
    </PagamentoLoteContext.Provider>
  );
}
